import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Camera, Trash2, ImagePlus, X } from 'lucide-react';
import { ProTag } from '@/components/ui/pro-tag';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useCamera } from '@/hooks/useCamera';
import { ImageCropModal } from '@/components/ui/ImageCropModal';
import { toast } from 'sonner';
import { StorageImage } from '@/components/ui/StorageImage';
import { createUuid } from '@/lib/uuid';

export interface GeneralPhoto {
  id: string;
  imageUrl: string;
  title?: string;
  description?: string;
}

interface GeneralPhotosSectionProps {
  photos: GeneralPhoto[];
  onChange: (photos: GeneralPhoto[]) => void;
  disabled?: boolean;
  isProUser?: boolean;
  maxPhotos?: number;
  /** Called when non-PRO user tries to add photo - opens upgrade modal */
  onProFeatureClick?: () => void;
}

const MAX_PHOTOS = 10;

export function GeneralPhotosSection({
  photos,
  onChange,
  disabled = false,
  isProUser = false,
  maxPhotos = MAX_PHOTOS,
  onProFeatureClick,
}: GeneralPhotosSectionProps) {
  const { capturePhoto, isCapturing } = useCamera();
  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [rawPhotoUrl, setRawPhotoUrl] = useState<string | null>(null);
  const [pendingPhoto, setPendingPhoto] = useState<string | null>(null);
  const [photoTitle, setPhotoTitle] = useState('');
  const [photoDescription, setPhotoDescription] = useState('');
  const [editingPhotoId, setEditingPhotoId] = useState<string | null>(null);

  const handleAddPhoto = async () => {
    // Block non-PRO users and show upgrade modal
    if (!isProUser) {
      if (onProFeatureClick) {
        onProFeatureClick();
      }
      return;
    }
    
    if (photos.length >= maxPhotos) {
      toast.warning(`Limite de ${maxPhotos} fotos gerais atingido.`);
      return;
    }

    const result = await capturePhoto({
      quality: 85,
      source: 'prompt',
    });

    if (result?.dataUrl) {
      // Open crop modal first
      setRawPhotoUrl(result.dataUrl);
      setShowCropModal(true);
    }
  };

  const handleCropComplete = (croppedImageUrl: string) => {
    // After cropping, show metadata modal
    setPendingPhoto(croppedImageUrl);
    setRawPhotoUrl(null);
    setPhotoTitle('');
    setPhotoDescription('');
    setShowMetadataModal(true);
  };

  const handleCropCancel = () => {
    setRawPhotoUrl(null);
  };

  const handleSavePhoto = () => {
    if (editingPhotoId) {
      // Editing existing photo
      const updatedPhotos = photos.map(p => 
        p.id === editingPhotoId 
          ? { ...p, title: photoTitle.trim() || undefined, description: photoDescription.trim() || undefined }
          : p
      );
      onChange(updatedPhotos);
      setShowMetadataModal(false);
      setEditingPhotoId(null);
      setPendingPhoto(null);
      setPhotoTitle('');
      setPhotoDescription('');
      toast.success('Foto atualizada!');
      return;
    }

    if (!pendingPhoto) return;

    const newPhoto: GeneralPhoto = {
      id: createUuid(),
      imageUrl: pendingPhoto,
      title: photoTitle.trim() || undefined,
      description: photoDescription.trim() || undefined,
    };

    onChange([...photos, newPhoto]);
    setShowMetadataModal(false);
    setPendingPhoto(null);
    setPhotoTitle('');
    setPhotoDescription('');
    toast.success('Foto adicionada!');
  };

  const handleEditPhoto = (photo: GeneralPhoto) => {
    setEditingPhotoId(photo.id);
    setPendingPhoto(photo.imageUrl);
    setPhotoTitle(photo.title || '');
    setPhotoDescription(photo.description || '');
    setShowMetadataModal(true);
  };

  const handleRemovePhoto = (id: string) => {
    onChange(photos.filter((p) => p.id !== id));
    toast.info('Foto removida.');
  };

  const canAddPhotos = isProUser && photos.length < maxPhotos;

  if (disabled) {
    if (photos.length === 0) return null;
    
    // Read-only view
    return (
      <Card className="rounded-2xl border border-border/60 bg-card shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Camera className="h-4 w-4" />
            Fotos / anexos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {photos.map((photo) => (
              <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden bg-muted">
                <StorageImage
                  src={photo.imageUrl}
                  alt={photo.title || 'Foto'}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="rounded-2xl border border-border/60 bg-card shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Camera className="h-4 w-4" />
              Fotos / anexos
            </CardTitle>
            <span className="text-xs text-muted-foreground">
              {photos.length}/{maxPhotos}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          {/* Grade única: as fotos e o tile "Adicionar" no mesmo fluxo, como no
              resto da tela. O botão de largura cheia acima da grade brigava com o
              botão de salvar do rodapé pela atenção. */}
          <div className="grid grid-cols-3 gap-2">
            {photos.map((photo) => (
              <div key={photo.id} className="relative">
                <button
                  type="button"
                  className="h-20 w-full overflow-hidden rounded-xl border border-border/60 bg-muted"
                  onClick={() => handleEditPhoto(photo)}
                >
                  <StorageImage
                    src={photo.imageUrl}
                    alt={photo.title || 'Foto'}
                    className="h-full w-full object-cover"
                  />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemovePhoto(photo.id);
                  }}
                  className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-white shadow"
                  aria-label="Remover foto"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
                {photo.title && (
                  <span className="mt-0.5 block w-full truncate text-center text-[10px] text-muted-foreground">
                    {photo.title}
                  </span>
                )}
              </div>
            ))}

            <button
              type="button"
              onClick={handleAddPhoto}
              disabled={isCapturing || (isProUser && photos.length >= maxPhotos)}
              className="relative flex h-20 flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-primary/40 text-[11px] text-primary transition-colors hover:bg-primary/5 disabled:opacity-60"
            >
              <Camera className="h-5 w-5" />
              {isCapturing ? 'Capturando...' : 'Adicionar'}
              {!isProUser && <ProTag className="absolute right-1 top-1" />}
            </button>
          </div>

          {photos.length === 0 && (
            <p className="mt-2 text-[11px] text-muted-foreground">
              Fotos gerais do projeto ou da vistoria — entram no PDF depois dos itens.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Metadata Modal */}
      <Dialog open={showMetadataModal} onOpenChange={(open) => {
        if (!open) {
          setEditingPhotoId(null);
          setPendingPhoto(null);
          setPhotoTitle('');
          setPhotoDescription('');
        }
        setShowMetadataModal(open);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingPhotoId ? 'Editar foto' : 'Adicionar foto'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Preview */}
            {pendingPhoto && (
              <div className="relative aspect-square w-full max-w-[200px] mx-auto rounded-lg overflow-hidden bg-muted">
                <StorageImage
                  src={pendingPhoto}
                  alt="Preview"
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="photo-title">Título (opcional)</Label>
              <Input
                id="photo-title"
                placeholder="Ex: Vista frontal"
                value={photoTitle}
                onChange={(e) => setPhotoTitle(e.target.value)}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="photo-description">Descrição (opcional)</Label>
              <Textarea
                id="photo-description"
                placeholder="Ex: Estado atual da fachada..."
                value={photoDescription}
                onChange={(e) => setPhotoDescription(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button3D
              color="gray"
              onClick={() => {
                setShowMetadataModal(false);
                setEditingPhotoId(null);
                setPendingPhoto(null);
                setPhotoTitle('');
                setPhotoDescription('');
              }}
            >
              Cancelar
            </Button3D>
            <Button3D color="green" onClick={handleSavePhoto}>
              {editingPhotoId ? 'Salvar' : 'Adicionar'}
            </Button3D>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Crop Modal */}
      {rawPhotoUrl && (
        <ImageCropModal
          open={showCropModal}
          onOpenChange={setShowCropModal}
          imageSrc={rawPhotoUrl}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
          aspectRatio={1}
        />
      )}
    </>
  );
}
